/**
 * Nuova commessa — form a 4 step in Accordion (Anagrafica / Planning / Economics / Note).
 * Crea una `project-entities` con i nuovi 25 attributi + relationships customer/category.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Accordion, Button, Card, Group, NumberInput, Select, Stack, Switch, TagsInput, TextInput, Textarea, Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import { createOne, fetchList } from '@/shared/api/jsonapi/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { useDirtyFormGuard } from '@/shared/hooks/useDirtyFormGuard';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/projects/new')({ component: NewProjectPage });

const schema = z.object({
  // Anagrafica
  code: z.string().min(1, 'Obbligatorio'),
  name: z.string().min(1, 'Obbligatorio'),
  description: z.string().optional(),
  customerName: z.string().optional(),
  customerId: z.string().nullable().optional(),
  categoryId: z.string().nullable().optional(),
  projectManagerId: z.string().nullable().optional(),
  priority: z.string().optional(),
  healthStatus: z.string().optional(),
  status: z.string().min(1),
  // Planning
  startDate: z.date().nullable().optional(),
  endDate: z.date().nullable().optional(),
  baselineStart: z.date().nullable().optional(),
  baselineEnd: z.date().nullable().optional(),
  deadline: z.date().nullable().optional(),
  plannedHours: z.number().nonnegative().nullable().optional(),
  // Economics
  budget: z.number().nonnegative().nullable().optional(),
  budgetCost: z.number().nonnegative().nullable().optional(),
  estimatedRevenue: z.number().nullable().optional(),
  estimatedCost: z.number().nullable().optional(),
  currency: z.string().optional(),
  contractValue: z.number().nullable().optional(),
  orderNumber: z.string().optional(),
  contractDate: z.date().nullable().optional(),
  paymentTerms: z.string().optional(),
  isBillable: z.boolean(),
  // Note
  notes: z.string().optional(),
  tags: z.array(z.string()).optional(),
});
type FormVals = z.infer<typeof schema>;

interface Tenant extends Denormalized { type: 'tenants'; name?: string; businessName?: string }
interface Category extends Denormalized { type: 'project-categories'; name?: string; code?: string }
interface User extends Denormalized { type: 'users'; firstName?: string; lastName?: string; email?: string }

const STATUS_OPTIONS = [
  { value: 'open', label: 'Aperta' },
  { value: 'in-progress', label: 'In corso' },
  { value: 'on-hold', label: 'In pausa' },
  { value: 'closed', label: 'Chiusa' },
  { value: 'archived', label: 'Archiviata' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const HEALTH_OPTIONS = [
  { value: 'green', label: 'On track' },
  { value: 'yellow', label: 'A rischio' },
  { value: 'red', label: 'Critico' },
];

const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP', 'CHF'].map((c) => ({ value: c, label: c }));

function NewProjectPage() {
  const navigate = useNavigate();
  const form = useForm<FormVals>({
    mode: 'controlled',
    initialValues: {
      code: '', name: '', description: '', customerName: '',
      customerId: null, categoryId: null, projectManagerId: null,
      priority: 'medium', healthStatus: 'green', status: 'open',
      startDate: null, endDate: null, baselineStart: null, baselineEnd: null, deadline: null,
      plannedHours: null,
      budget: null, budgetCost: null, estimatedRevenue: null, estimatedCost: null,
      currency: 'EUR', contractValue: null, orderNumber: '', contractDate: null, paymentTerms: '',
      isBillable: true,
      notes: '', tags: [],
    },
    validate: zodResolver(schema),
  });

  const tenantsQ = useQuery({
    queryKey: ['tenants-for-projects'],
    queryFn: () => fetchList<Tenant>('tenants', { page: { size: 100 } }),
  });
  const categoriesQ = useQuery({
    queryKey: ['project-categories'],
    queryFn: () => fetchList<Category>('project-categories', { page: { size: 100 } }),
  });
  const usersQ = useQuery({
    queryKey: ['users-for-projects'],
    queryFn: () => fetchList<User>('users', { page: { size: 100 } }),
  });

  const tenantOpts = (tenantsQ.data?.data ?? []).map((t) => ({ value: t.id, label: t.businessName ?? t.name ?? t.id }));
  const categoryOpts = (categoriesQ.data?.data ?? []).map((c) => ({ value: c.id, label: c.name ?? c.code ?? c.id }));
  const userOpts = (usersQ.data?.data ?? []).map((u) => ({
    value: u.id,
    label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
  }));

  const create = useMutation({
    mutationFn: (v: FormVals) => {
      const relationships: Record<string, { data: { type: string; id: string } | null }> = {};
      if (v.customerId) relationships.customer = { data: { type: 'tenants', id: v.customerId } };
      if (v.categoryId) relationships.category = { data: { type: 'project-categories', id: v.categoryId } };
      // BE v1.8.0: projectManager passa via relationship (era attributes.projectManagerId).
      if (v.projectManagerId) relationships.projectManager = { data: { type: 'users', id: v.projectManagerId } };
      return createOne('project-entities', {
        attributes: {
          code: v.code,
          name: v.name,
          description: v.description ?? null,
          customerName: v.customerName ?? null,
          priority: v.priority ?? null,
          healthStatus: v.healthStatus ?? null,
          status: v.status,
          startDate: v.startDate ? dayjs(v.startDate).format('YYYY-MM-DD') : null,
          endDate: v.endDate ? dayjs(v.endDate).format('YYYY-MM-DD') : null,
          baselineStart: v.baselineStart ? dayjs(v.baselineStart).format('YYYY-MM-DD') : null,
          baselineEnd: v.baselineEnd ? dayjs(v.baselineEnd).format('YYYY-MM-DD') : null,
          deadline: v.deadline ? dayjs(v.deadline).format('YYYY-MM-DD') : null,
          plannedHours: v.plannedHours,
          budget: v.budget,
          budgetCost: v.budgetCost,
          estimatedRevenue: v.estimatedRevenue,
          estimatedCost: v.estimatedCost,
          currency: v.currency ?? 'EUR',
          contractValue: v.contractValue,
          orderNumber: v.orderNumber || null,
          contractDate: v.contractDate ? dayjs(v.contractDate).format('YYYY-MM-DD') : null,
          paymentTerms: v.paymentTerms || null,
          isBillable: v.isBillable,
          notes: v.notes || null,
          freeFieldString1: v.tags?.length ? v.tags.join(',') : null,
          isActive: true,
        },
        relationships: Object.keys(relationships).length ? relationships : undefined,
      });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Commessa creata' });
    },
    onError: (err) => {
      const msg = err instanceof JsonApiHttpError ? (err.errors[0]?.detail ?? 'Errore') : 'Errore';
      notifications.show({ color: 'red', message: msg });
    },
  });

  // Protegge il form da uscite accidentali con dati non salvati.
  // Disattivato dopo la creazione: il redirect a /projects non va bloccato.
  useDirtyFormGuard(form.isDirty() && !create.isSuccess);

  // Navigazione post-creazione in effect (non in onSuccess) così il blocker
  // viene ri-registrato con il guard disattivato prima del navigate.
  useEffect(() => {
    if (create.isSuccess) void navigate({ to: '/projects' });
  }, [create.isSuccess, navigate]);

  return (
    <Stack>
      <Title order={1}>Nuova commessa</Title>
      <Card withBorder padding="lg" radius="md">
        <form onSubmit={form.onSubmit((v) => void create.mutate(v))}>
          <Accordion defaultValue={['anagrafica']} multiple variant="separated" chevronPosition="left">
            <Accordion.Item value="anagrafica">
              <Accordion.Control>Anagrafica</Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <Group grow>
                    <TextInput label="Codice commessa" required {...form.getInputProps('code')} />
                    <TextInput label="Nome" required {...form.getInputProps('name')} />
                  </Group>
                  <Group grow>
                    <Select
                      label="Cliente"
                      placeholder="Seleziona tenant cliente"
                      data={tenantOpts}
                      searchable
                      clearable
                      {...form.getInputProps('customerId')}
                    />
                    <TextInput label="Cliente (testo libero)" {...form.getInputProps('customerName')} />
                  </Group>
                  <Group grow>
                    <Select
                      label="Categoria"
                      placeholder="Seleziona categoria"
                      data={categoryOpts}
                      searchable
                      clearable
                      {...form.getInputProps('categoryId')}
                    />
                    <Select
                      label="Project Manager"
                      placeholder="Seleziona PM"
                      data={userOpts}
                      searchable
                      clearable
                      {...form.getInputProps('projectManagerId')}
                    />
                  </Group>
                  <Group grow>
                    <Select label="Priorità" data={PRIORITY_OPTIONS} {...form.getInputProps('priority')} />
                    <Select label="Stato salute" data={HEALTH_OPTIONS} {...form.getInputProps('healthStatus')} />
                    <Select label="Stato" data={STATUS_OPTIONS} {...form.getInputProps('status')} />
                  </Group>
                  <Textarea label="Descrizione" autosize minRows={2} {...form.getInputProps('description')} />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="planning">
              <Accordion.Control>Planning</Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <Group grow>
                    <DatePickerInput label="Inizio" clearable {...form.getInputProps('startDate')} />
                    <DatePickerInput label="Fine" clearable {...form.getInputProps('endDate')} />
                    <DatePickerInput label="Deadline" clearable {...form.getInputProps('deadline')} />
                  </Group>
                  <Group grow>
                    <DatePickerInput label="Baseline inizio" clearable {...form.getInputProps('baselineStart')} />
                    <DatePickerInput label="Baseline fine" clearable {...form.getInputProps('baselineEnd')} />
                    <NumberInput label="Ore pianificate" min={0} decimalScale={2} {...form.getInputProps('plannedHours')} />
                  </Group>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="economics">
              <Accordion.Control>Economics</Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <Group grow>
                    <NumberInput label="Budget (ore)" min={0} {...form.getInputProps('budget')} />
                    <NumberInput label="Budget costo" min={0} decimalScale={2} {...form.getInputProps('budgetCost')} />
                    <Select label="Valuta" data={CURRENCY_OPTIONS} {...form.getInputProps('currency')} />
                  </Group>
                  <Group grow>
                    <NumberInput label="Ricavi stimati" decimalScale={2} {...form.getInputProps('estimatedRevenue')} />
                    <NumberInput label="Costi stimati" decimalScale={2} {...form.getInputProps('estimatedCost')} />
                  </Group>
                  <Group grow>
                    <NumberInput label="Valore contratto" decimalScale={2} {...form.getInputProps('contractValue')} />
                    <TextInput label="N. ordine" {...form.getInputProps('orderNumber')} />
                  </Group>
                  <Group grow>
                    <DatePickerInput label="Data contratto" clearable {...form.getInputProps('contractDate')} />
                    <TextInput label="Termini di pagamento" placeholder="es. 30gg DF" {...form.getInputProps('paymentTerms')} />
                  </Group>
                  <Switch label="Fatturabile" {...form.getInputProps('isBillable', { type: 'checkbox' })} />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="note">
              <Accordion.Control>Note</Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <Textarea label="Note" autosize minRows={3} {...form.getInputProps('notes')} />
                  <TagsInput label="Tag" placeholder="Premi Invio per aggiungere" {...form.getInputProps('tags')} />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Group justify="flex-end" mt="lg">
            <Button variant="subtle" onClick={() => void navigate({ to: '/projects' })}>Annulla</Button>
            <Button type="submit" loading={create.isPending}>Crea</Button>
          </Group>
        </form>
      </Card>
    </Stack>
  );
}
